<template>
  <div class="relative">
    <!-- Fixed Hero Background with Parallax Y -->
    <Motion
      class="fixed inset-0 z-0"
      :style="{ transform: `translateY(${heroY}px)` }"
    >
      <HeroSection />
    </Motion>
    <!-- Scrollable Content -->
    <div class="relative z-10">
      <!-- Spacer matching viewport height -->
      <div :style="{ height: windowHeight + 'px' }" />

      <!-- Main content -->
      <div class="bg-background">
        <AboutSection
          :currentPage="currentPage"
          @navigate="$emit('navigate', $event)"
        />

        <ProjectSection
          title="The Rwanda Build Program (RWbuild)"
          subtitle="A Tech Hub & Startup Studio in Kigali, Rwanda"
          description="Rob is the founder and director of RWBuild, a tech hub and startup studio that supports aspiring entrepreneurs in building software companies from the ground up. RWBuild offers a range of resources and expertise to help founders and their teams throughout the ideation and launch process, with the goal of creating successful and sustainable startups that will be venture-ready."
          :highlights="[
            'Mentored 200+ founders/teams',
            'Trained 130+ developers',
            '30+ community members',
            '15+ active full members(resident teams funded)',
          ]"
          website-url="https://rwandabuildprogram.rw"
          website-label="Visit RWBuild"
        />

        <ProjectSection
          title="Guhemba Ltd."
          subtitle="CEO & Founder"
          description="Currently, Rob is focused on the future of the Cashless Society as Founder and Interim CEO of Guhemba Ltd. Guhemba is a cashless payment solution designed for the culture of Rwanda and greater Africa. This massive payment service is designed to truly help Rwanda/Africa reach its goal of becoming a cashless society providing a host of fintech services for merchants and customers."
          :highlights="[
            'Traction: 1,800 users',
            '31,000+ transactions',
            'Currently seeking funding & investors',
          ]"
          :is-reversed="true"
          website-url="https://www.guhemba.rw"
          website-label="Visit Guhemba.rw"
        />

        <ProjectSection
          title="912Rwanda Project"
          subtitle="Core Architect & Design Product Manager"
          description="912Rwanda is a groundbreaking initiative aimed at transforming pre-hospital emergency care in Rwanda through the integration of advanced & innovation technology. Developed in partnership with the Ministry of Health, the Rwanda Biomedical Centre, and both local and international academic institutions, this project enhances emergency medical response, reduces response times, and ultimately saves lives. As the national emergency ambulance dispatch platform, it showcases RWBuild's significant role in its inception, engineering, and completion in Kigali, led by Rob Rickard and the RWBuild team."
          :highlights="[
            '+14,000 emergency ambulance managed',
            'Partnership with Rwanda Ministry of Health and Rwanda Biomedical Centre',
            'Funded by the US NIR & UK NIHR',
            'International Global Health Partners, University of Utah & University of Birmingham',
          ]"
          website-url="https://912Rwanda.rw"
          website-label="Visit 912Rwanda"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { useHead } from "@vueuse/head";
import { Motion } from "@motionone/vue";
import HeroSection from "./HeroSection.vue";
import AboutSection from "./AboutSection.vue";
import ProjectSection from "./ProjectSection.vue";

useHead({
  title: "Home | Rob Rickard",
  meta: [
    {
      name: "description",
      content:
        "Official portfolio of Rob Rickard — tech entrepreneur, founder of Guhemba Ltd, RWBuild, and contributor to 912Rwanda. Building innovation, fintech, and digital systems in Kigali, Rwanda.",
    },
    { property: "og:title", content: "Rob Rickard | Tech Entrepreneur" },
    {
      property: "og:description",
      content:
        "Founder of RWBuild & Guhemba. Building impactful technology, fintech, and innovation systems in Rwanda and Africa.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://robrickard.com/" },
    {
      property: "og:image",
      content: "https://robrickard.com/img/robPortrait.65dd6588.png",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: "Rob Rickard | Tech Entrepreneur & Founder",
    },
    {
      name: "twitter:description",
      content:
        "Founder of RWBuild & Guhemba. Building impactful technology and innovation systems in Rwanda and Africa.",
    },
    {
      name: "twitter:image",
      content: "https://robrickard.com/img/robPortrait.65dd6588.png",
    },
  ],
  link: [{ rel: "canonical", href: "https://robrickard.com/" }],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Rob Rickard",
        url: "https://robrickard.com/",
        image: "https://robrickard.com/img/robPortrait.65dd6588.png",
        jobTitle: "Tech Entrepreneur & Product Strategist",
        worksFor: {
          "@type": "Organization",
          name: "Guhemba Ltd",
        },
        alumniOf: {
          "@type": "Organization",
          name: "RWBuild",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Kigali",
          addressCountry: "Rwanda",
        },
        sameAs: [
          "https://www.linkedin.com/",
          "https://twitter.com/",
          "https://github.com/",
        ],
      }),
    },
  ],
});

const windowHeight = ref(window.innerHeight);
const heroY = ref(0);
function updateScroll() {
  const scrollTop = window.scrollY || window.pageYOffset;
  heroY.value = -scrollTop * 0.5;
}
function updateWindowHeight() {
  windowHeight.value = window.innerHeight;
}
onMounted(() => {
  window.addEventListener("scroll", updateScroll);
  window.addEventListener("resize", updateWindowHeight);
  updateScroll();
});
onUnmounted(() => {
  window.removeEventListener("scroll", updateScroll);
  window.removeEventListener("resize", updateWindowHeight);
});
</script>
